import { asError } from "../../Helpers/Functions/error"
import { FilterAndSortExpression } from "../Client/FilterAndSortExpression"
import { Result } from "../Utility/Result"
import { APIOrgMemberRole } from "./APIOrgMember"

export type CollaboratorRole = "OWNER" | "ADMIN" | "INSTRUCTOR" | "MEMBER"

export const CollaboratorRole = {
    OWNER: "OWNER",
    ADMIN: "ADMIN",
    INSTRUCTOR: "INSTRUCTOR",
    MEMBER: "MEMBER"
} as const

export interface RolePermissions {
    name: string
    object: "Account" | "Project" | "Data point" | "Ground-truth data"
    owner: boolean
    admin: boolean
    reviewer: boolean
    labeler: boolean
}

export enum CollaboratorStatus {
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE",
    INVALID = "INVALID",
    DELETED = "DELETED"
}

export interface APICollaborationModel {
    accepted: string
    appId: string
    /**
     * The value will be `null` until user signs up
     */
    collaboratorEmail: string | null
    collaboratorId: number
    collaboratorRole: CollaboratorRole
    collaboratorStatus: CollaboratorStatus
    created: string
    id: number
    invited: string
    invitedEmail: string
    ownerEmail: string
    ownerId: number
    role: CollaboratorRole
    orgRole: APIOrgMemberRole | null
}

export function getCollaborationID(item: APICollaborationModel): string {
    return String(item.id)
}

export type CollaborationOrderBy = "asc" | "desc"
export type CollaborationSortBy = keyof Pick<
    APICollaborationModel,
    "invitedEmail" | "collaboratorRole" | "collaboratorStatus"
>

export interface APICollaborationParams {
    sort: CollaborationSortBy
    desc: boolean
}

export const defaultParams: APICollaborationParams = {
    sort: "invitedEmail",
    desc: false
}

function isValidSort(
    sortBy: keyof APICollaborationModel
): sortBy is CollaborationSortBy {
    return ["invitedEmail", "collaboratorRole", "collaboratorStatus"].includes(
        sortBy
    )
}

export function decodeCollaborationParams({
    sort
}: FilterAndSortExpression<APICollaborationModel>): Result<APICollaborationParams> {
    const { desc, sortBy } = sort

    try {
        return Result.ok<APICollaborationParams>({
            sort: !isValidSort(sortBy) ? "invitedEmail" : sortBy,
            desc
        })
    } catch (e) {
        return Result.error(asError(e).message)
    }
}

export function compareCollaboration(
    sort: APICollaborationParams
): (aCollab: APICollaborationModel, bCollab: APICollaborationModel) => number {
    return (aCollab, bCollab) => {
        const aValue = String(aCollab[sort.sort])
        const bValue = String(bCollab[sort.sort])

        return (
            (sort.desc ? -1 : 1) *
            (aValue > bValue ? 1 : aValue < bValue ? -1 : 0)
        )
    }
}
